import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { submitContactForm } from '../utils/api';
import { trackLead } from '../utils/pixel';
import { Seo } from '../components/Seo';

const Contact = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    // Validate name
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (formData.name.trim().length < 2) {
      toast.error('Please enter a valid name (at least 2 characters)');
      return false;
    }

    // Validate email
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate phone (optional but if provided, must be valid)
    if (formData.phone.trim()) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        toast.error('Please enter a valid phone number (at least 10 digits)');
        return false;
      }
    }

    // Validate message
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return false;
    }
    if (formData.message.trim().length < 10) {
      toast.error('Please enter a more detailed message (at least 10 characters)');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await submitContactForm(formData);
      if (response.success) {
        trackLead('Contact Form');
        toast.success(response.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: '541-548-8215',
      subtext: 'Mon-Thu 8am-5pm • Fri 8am-3pm',
      link: 'tel:5415488215'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'kholloway@aicinsagency.com',
      subtext: 'We reply within 24 hours',
      link: 'mailto:kholloway@aicinsagency.com'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: '2801 NE 3rd St',
      subtext: 'Prineville, OR 97754',
      link: '#'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Monday - Thursday',
      subtext: '8:00 AM - 5:00 PM',
      link: '#'
    },
    {
      icon: Clock,
      title: 'Friday Hours',
      details: 'Friday',
      subtext: '8:00 AM - 3:00 PM',
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Contact Kerry M Holloway Agency | Insurance Agent in Prineville, Oregon"
        description="Contact Kerry M Holloway Agency at 541-548-8215 or kholloway@aicinsagency.com. Located at 2801 NE 3rd St, Prineville, OR. Serving Prineville, Bend, Redmond, and Madras with personal insurance guidance."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="bg-white" style={{ paddingTop: "360px", paddingBottom: "48px" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
            <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full">
              <MessageSquare className="h-4 w-4 text-red-800" />
              <span className="text-sm font-medium text-red-800">Get In Touch</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1F1F]">
              We're Here to Help
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Have a question or need a quote? Call during business hours or send a message below, and I'll get back to you as soon as I can.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {contactInfo.map((info) => (
              <Card key={info.title} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-red-200">
                <CardContent className="p-6 text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br bg-[#D94B5A] rounded-xl shadow-lg mx-auto">
                    <info.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="px-2">
                    <h3 className="font-bold text-[#1F1F1F] mb-2">{info.title}</h3>
                    {info.link !== '#' ? (
                      <a 
                        href={info.link}
                        className="text-[#D94B5A] hover:text-red-800 font-medium transition-colors block text-xs sm:text-sm break-all"
                        style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        {info.details}
                      </a>
                    ) : (
                      <p className="text-[#1F1F1F] font-medium text-sm">{info.details}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">{info.subtext}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br bg-[#F7F7F7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1F1F1F] mb-4">
                Send Us a Message
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and I'll get back to you as soon as I can.
              </p>
            </div>

            <Card className="shadow-2xl border-2 border-gray-100">
              <CardContent className="p-8 lg:p-12">
                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                  {/* Name & Email */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1F1F1F] font-semibold">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1F1F1F] font-semibold">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="text"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                      />
                    </div>
                  </div>

                  {/* Phone & Subject */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1F1F1F] font-semibold">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-[#1F1F1F] font-semibold">
                        Subject
                      </Label>
                      <Input
                        id="subject"
                        type="text"
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        className="h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1F1F1F] font-semibold">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us what you need help with..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="min-h-40 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A] resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r bg-[#D94B5A] hover:bg-[#C43D4D] text-white shadow-lg hover:shadow-xl transition-all duration-300 group h-14 text-lg"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-600 text-center">
                    * Required fields. We typically respond within 24 hours during business days.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1F1F1F] mb-4">
                Visit Our Office
              </h2>
              <p className="text-lg text-gray-600">
                We'd love to meet you in person. Schedule an appointment or drop by during business hours.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-2xl h-[400px] bg-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2872.8!2d-120.8347!3d44.2998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54b8c32b04a89e69%3A0x9876543210!2s2801%20NE%203rd%20St%2C%20Prineville%2C%20OR%2097754!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kerry M Holloway Insurance Agency Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
